import { forwardRef, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ProfileDropdown } from "../modals/ProfileDropdown";
import { useMainContext } from "../../core/contexts/main";
import { useWindowSize } from "../../core/hooks/useWindowSize";
import useClickOutside from "../../core/hooks/useClickOutside";
import { useProfileQuery } from "../../core/services/react-query/useProfileQuery";
import { HOME } from "../../core/constants/urls";
import { CLOSE_SIDEBAR_SIZE, MOBILE_SIZE } from "../../core/constants/common";
import {
	Body,
	DropBody,
	Flex,
	HeaderMenuBody
} from "../../styles/layout-styles/HeaderStyles";
import Text from "../../core/utils/Text";
import { useAuthContext } from "../../core/contexts/auth";
import { FlexColumn } from "../../styles/CommonStyles";
import { FaChevronDown, FaTwitter } from "react-icons/fa";
import { FaTelegram, FaYoutube, FaInstagram, FaCoins, FaDatabase } from "react-icons/fa";
import { RespBody } from "../../styles/layout-styles/SideBarStyles.js";
import RespNavigation from "./RespNavigation.jsx";
import GridIcon from "../icons/GridIcon.jsx";
import UserSolidIcon from "../icons/UserSolidIcon.jsx";
import BellIcon from "../icons/BellIcon.jsx";
import NotificationDropdown from "../../pages/notifications/components/NotificationDropdown.jsx";
import CogIcon from "../icons/CogIcon.jsx";
import LangSwitcher from "../common/LangSwitcher.jsx";
import RLangSwitcher from "../common/RLangSwitcher.jsx";
import MobileModal from "./MobileModal.jsx";
import { GetStartedEllipse } from "../common/GetStartedEllipse.jsx";

import { ReactComponent as MedalIcon } from "../../assets/images/svg-icons/medal.svg";
import { ReactComponent as HeartIcon } from "../../assets/images/svg-icons/heart.svg";
import { ReactComponent as FaqIcon } from "../../assets/images/svg-icons/faq-circle.svg";
import { ReactComponent as DocumentIcon } from "../../assets/images/svg-icons/document.svg";
import { ReactComponent as EducationIcon } from "../../assets/images/svg-icons/education.svg";
import { ReactComponent as PhoneIcon } from "../../assets/images/svg-icons/phone.svg";
import WebsiteIcon from "../icons/WebsiteIcon.jsx";

const variants = {
	in: {
		y: 0,
		x: '',
		transition: { duration: 0.3 },
	},
	out: {
		y: '0',
		x: '100vw',
		transition: { duration: 0.3 },
	},
}

export const ICON_MAP = {
	telegram: <FaTelegram className="w-7 h-7 text-sky-500" />,
	x: <FaTwitter className="w-7 h-7 text-black dark:text-white" />,
	youtube: <FaYoutube className="w-7 h-7 text-red-500" />,
	instagram: <FaInstagram className="w-7 h-7 text-pink-500" />,
	"coin-market-cap": <FaCoins className="w-7 h-7 text-yellow-500" />,
	"crunch-base": <FaDatabase className="w-7 h-7 text-indigo-500" />,
	"our-services": <MedalIcon width={24} height={24} />,
	"about-us": <HeartIcon width={24} height={24} />,
	"faq": <FaqIcon width={24} height={24} />,
	"blog": <DocumentIcon width={24} height={24} />,
	"academy": <EducationIcon width={24} height={24} />,
	"contact-us": <PhoneIcon width={24} height={24} />,
};


export const HEADER_LINKS = [
	{ tid: "home", href: '/' },
	{ tid: "all-analyzers", href: HOME + "#analyzers", },
	{
		tid: "our-services", href: "", hasMega: true,
		megaOptions: [
			{ tid: "our-services", href: HOME + "our-services", icon: <MedalIcon width={24} height={24} /> },
			{ tid: "about-us", href: HOME + "about-us", icon: <HeartIcon width={24} height={24} /> },
			{
				tid: "faq",
				href: HOME + "#faq",
				icon: <FaqIcon width={24} height={24} />,
			},
			{
				tid: "blog",
				href: HOME + "#",
				icon: <DocumentIcon width={24} height={24} />,
			},
			{ tid: "academy", href: HOME + "#", icon: <EducationIcon width={24} height={24} /> },
			{ tid: "contact-us", href: HOME + "contact-us", icon: <PhoneIcon width={24} height={24} /> },
		],
	},
	{ tid: "demo", href: HOME },
]

const MasterHeader = ({ pageTitle }) => {
	const { main: { lang, theme, setTheme }, profile: { token } } = useMainContext();

	const { width } = useWindowSize();
	const navigate = useNavigate();
	const location = useLocation();


	const [isOpen, setIsMenuOpen] = useState(false);

	const [profileOpen, setProfileOpen] = useState(false);
	const [notificationOpen, setNotificationOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);

	// Individual refs for each dropdown/toggle
	const menuOutRef = useRef();
	const menuToggleRef = useRef();


	const profileOutRef = useRef();
	const profileToggleRef = useRef();

	const settingsOutRef = useRef();
	const settingsToggleRef = useRef();

	const notificationsOutRef = useRef();
	const notificationsToggleRef = useRef();


	useClickOutside(menuOutRef, menuToggleRef, () => setIsMenuOpen(false));
	useClickOutside(profileOutRef, profileToggleRef, () => setProfileOpen(false));
	useClickOutside(settingsOutRef, settingsToggleRef, () => setSettingsOpen(false));
	useClickOutside(notificationsOutRef, notificationsToggleRef, () => setNotificationOpen(false));

	const onThemeChange = () => setTheme(theme === 'dark' ? 'light' : 'dark');

	const { data: profile } = useProfileQuery();
	const { logout } = useAuthContext();

	const onProfileItemsClicked = (type, path = null) => {
		if (type === "exit") logout();
		else navigate(path);
	};

	return (
		<Body resp={width <= MOBILE_SIZE}>
			<Flex className={`items-center w-full justify-between h-full gap-2 md:gap-5`}>
				<img
					src={require(`../../assets/images/logo.png`)}
					width={width <= 1024 ? "100px" : "150px"}
					alt="Logo"
					className={`${width >= MOBILE_SIZE ? "" : "hidden"}`}
					style={{ cursor: 'pointer' }}
				/>

				{/* DESKTOP LINKS */}
				{
					width >= MOBILE_SIZE ?
						<div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-50">
							<HeaderMenuBody>
								{HEADER_LINKS.map((link) => {
									const isActive = location.pathname === link.href;
									const { hasMega } = link;
									if (hasMega) {
										return (
											<div className="flex justify-between items-center gap-2 group relative">
												<Text
													tid={link.tid}
													className={`rtl:font-semibold lg:text-lg transition-colors text-nowrap  cursor-pointer`}
												/>
												<FaChevronDown className="group-hover:rotate-180 transition-all  cursor-pointer" />
												<MegaMenu options={link.megaOptions} />
											</div>
										)
									}
									return (
										<Link key={link.tid} to={link.href} className="flex flex-col items-center relative">
											<Text
												tid={link.tid}
												className={`rtl:font-semibold lg:text-lg transition-colors text-nowrap ${isActive ? "text-mainColor" : ""
													}`}
											/>
										</Link>
									);
								})}
							</HeaderMenuBody>
						</div>
						: null
				}


				{(width <= MOBILE_SIZE && !!token) && (
					<>
						<Flex ref={menuOutRef}>
							<div ref={menuToggleRef} onClick={() => setIsMenuOpen(state => !state)} className={`flex items-center`}>
								<GridIcon size={30} />
							</div>
						</Flex>
						<div className="">
							<div className={`flex items-center`}>
								<Text tid={pageTitle} className="text-lg" />
							</div>
						</div>

					</>
				)}
				{width <= MOBILE_SIZE &&
					<div ref={settingsOutRef} className="relative rtl:mr-auto ltr:ml-auto">
						<button ref={settingsToggleRef} onClick={() => setSettingsOpen(state => !state)} className="btn md:gradient-border">
							<CogIcon size={21} />
						</button>
						{settingsOpen && (
							<SettingDropDown ref={settingsToggleRef} />
						)}
					</div>
				}

				<div ref={notificationsOutRef} className="relative md:rtl:mr-auto md:ltr:ml-auto">
					<button ref={notificationsToggleRef} onClick={() => setNotificationOpen(state => !state)} className="btn md:gradient-border">
						<BellIcon size={21} />
					</button>
					{notificationOpen && (
						<NotificationDropdown ref={notificationsToggleRef} isOpen={notificationOpen} onClose={() => setNotificationOpen(false)} />
					)}
				</div>



				<div ref={profileOutRef} className="relative">
					<button ref={profileToggleRef} onClick={() => setProfileOpen(state => !state)} className="bg-g lg:bg-gradient-1 btn lg:w-[150px] xl:w-[200px]">
						<UserSolidIcon size={21} />
						<Text tid={'your-profile'} className="text-sm hidden lg:inline rtl:font-bold" />
					</button>
					{profileOpen && (
						<ProfileDropdown
							lang={lang}
							width={width}
							profile={profile}
							onProfileItemsClicked={onProfileItemsClicked}
						/>
					)}
				</div>

			</Flex>

			{width <= MOBILE_SIZE && (
				<div ref={menuToggleRef}>
					<RespBody
						resp={width <= MOBILE_SIZE}
						variants={{ ...variants, in: { x: "-45%" } }}
						initial='out'
						animate={width < CLOSE_SIDEBAR_SIZE && (isOpen ? 'in' : 'out')}
					>
						<RespNavigation />
					</RespBody>
				</div>
			)}
		</Body>
	);
};



const SettingDropDown = forwardRef((props, togglerRef) => {
	const { main: { lang } } = useMainContext();
	const [isModalOpen, setIsModalOpen] = useState(false);

	const languages = {
		en: 'English',
		fa: 'فارسی',
	};

	return (
		<>
			<DropBody
				className="!w-[200px] p-5">
				<FlexColumn className="gap-2" >
					<Text tid={"language"} />
					<button
						onClick={() => setIsModalOpen(true)}
						className="flex items-center gap-2 py-2 px-3 border border-purpleBorderDark rounded-xl w-full text-left"
					>
						<WebsiteIcon size={20} />
						<span className="capitalize">{languages[lang]}</span>
						<div className="rtl:mr-auto ltr:ml-auto" />
						<FaChevronDown />
					</button>
				</FlexColumn>
				<div className="h-[2px] w-[90%] absolute left-1/2 -translate-x-1/2 bottom-0 bg-gradient-to-r from-primaryOrange/0 via-primaryOrange to-primaryOrange/0" />
			</DropBody>

			<MobileModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title="language"
			>
				<div ref={togglerRef}>
					<RLangSwitcher closeModal={() => setIsModalOpen(false)} />
				</div>
			</MobileModal>
		</>
	);
});



export const MegaMenu = ({ options }) => {

	return (
		<div className="absolute left-1/2 -translate-x-1/2 top-full overflow-hidden hidden group-hover:grid grid-cols-2 gap-y-5 gap-x-8 py-8 px-10 bg-[#0F0524] z-[9999] shadow-xl rounded-[24px] min-w-[600px]">
			<div className="absolute flex items-center justify-center left-0 top-0 z-[9999]">
				<GetStartedEllipse colorCode={"megamenu"} width={600} height={400} />
			</div>
			{options.map((item) => (
				<Link
					key={item.tid}
					href={item.href}
					rel="noopener noreferrer"
					className="flex flex-col gap-[2px] p-2 rounded-lg hover:bg-gray-500/20 transition-colors relative z-[99999]"
				>
					<div className="flex items-center gap-2">
						{item.icon}
						<Text tid={item.tid} className="rtl:font-semibold" />
					</div>
					<span className="text-sm font-medium">
						<Text tid={`${item.tid}-megamenu`} className="text-sm" />
					</span>
				</Link>
			))}
		</div>
	);
};

export default MasterHeader;
